<script setup lang="ts">
import {
  OFFERS_LIST_FETCH_ERROR,
  OFFERS_MARK_READ_ERROR,
} from '@/features/Notifications/lib/notificationsMessages';
import { INQUIRY_STATUS_NOTIFICATIONS } from '@/shared/constants/inquiryConstants';

import type {
  NotificationWithId,
  NotificationApiItem,
} from '@/features/Notifications/model/types/notifications';
import {
  isSupportedNotification,
  mapNotification,
} from '@/features/Notifications/lib/mapNotification';

import NotificationsList from '@/features/Notifications/ui/NotificationsList.vue';
import { useErrorHandler } from '@/shared/lib/errors';
import { useUserStore } from '@/entities/user/store';

const { $lawyerService } = useNuxtApp();

const isMobile: boolean = useIsMobile();
const { handleError } = useErrorHandler();
const userStore = useUserStore();
const notifications: Ref<NotificationWithId[]> = ref([]);
const isLoading: Ref<boolean> = ref(false);

const getOffersList = async (): Promise<void> => {
  isLoading.value = true;
  try {
    const { data } = await $lawyerService.getOffersList<NotificationApiItem[]>({
      status: INQUIRY_STATUS_NOTIFICATIONS,
      is_viewed: 0,
    });

    if (data) {
      notifications.value = data.filter(isSupportedNotification).map(mapNotification);
    }

    if (!notifications.value.length) userStore.setHasNotification(false);
  } catch (error) {
    handleError(error, { context: { action: OFFERS_LIST_FETCH_ERROR } });
  } finally {
    isLoading.value = false;
  }
};

const markAsViewed = async (): Promise<void> => {
  try {
    await $lawyerService.markAllAsViewed();
    userStore.setHasNotification(false);
    await getOffersList();
  } catch (error) {
    handleError(error, { context: { action: OFFERS_MARK_READ_ERROR } });
  }
};

const markOneAsViewed = async (
  id: number,
  roomId: number | string | null,
): Promise<void> => {
  try {
    await $lawyerService.markAllAsViewed(roomId ? { room_id: roomId } : {});
    notifications.value = notifications.value.filter((item: NotificationWithId) =>
      roomId ? item.roomId !== roomId : item.id !== id,
    );
    if (!notifications.value.length) userStore.setHasNotification(false);
  } catch (error) {
    handleError(error, { context: { action: OFFERS_MARK_READ_ERROR } });
  }
};

onMounted(() => {
  getOffersList();
});
</script>

<template>
  <div :class="['notifications', { _mobile: isMobile }]" data-testid="notifications">
    <div class="notifications__header">
      <span class="notifications__title" data-testid="notifications-title">
        Уведомления
      </span>
      <AppButton
        v-if="notifications.length"
        class="notifications__button"
        button-text="Пометить прочитанным"
        is-bordered
        is-white
        :disabled="isLoading"
        @click="markAsViewed"
      />
    </div>
    <NotificationsList
      :notifications="notifications"
      :is-loading="isLoading"
      @read="markOneAsViewed"
    />
  </div>
</template>

<style lang="less">
.notifications {
  .box-shadow();

  border-radius: @main-size;
  padding: @size-x5;
  background-color: #fff;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: @size-x2;
  }

  &__title {
    font-size: @size-x6;
    font-weight: 700;
    line-height: 130%;
  }

  &__button {
    max-width: 274px;
  }

  &._mobile {
    max-width: 540px;
    margin: 0 auto;
    padding: @size-x6 @size-x3;
  }

  &._mobile &__title {
    font-size: 18px;
  }
}
</style>
